import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { SideBar } from "../../components/AdminLayout/SideBar";
import { TopBar } from "../../components/AdminLayout/TopBar";
import { brandSubdomainBase, getRouterBasename } from "../../config";
import { useBrand } from "../../context/BrandContext";
import {
  adminCancelBooking,
  adminDeleteAvailability,
  adminDeleteOffering,
  adminListAvailabilities,
  adminListBookings,
  adminListOfferings,
  adminSaveAvailability,
  adminSaveOffering,
  adminSeedOfferings,
  adminStripeOnboarding,
  adminStripeStatus,
  adminGetRentalSettings,
  adminUpdateRentalSettings,
  adminListClubTables,
} from "../../services/rentalService";

const SERVICE_TYPES = ["AI_TRAINING", "MATCH_PLAY", "PRIVATE_COACHING", "CUSTOM"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDurationDisplay(offering) {
  const min = offering?.duration_min_minutes || 60;
  const max = offering?.duration_max_minutes || min;
  if (offering?.duration_label) return offering.duration_label;
  if (min === max) return String(min);
  return `${min}–${max}`;
}

function emptyOffering() {
  return {
    service_type: "CUSTOM",
    name_en: "",
    name_fr: "",
    name_de: "",
    name_es: "",
    name_nl: "",
    name_pt: "",
    description_en: "",
    price_cents: 2500,
    duration_min_minutes: 60,
    duration_max_minutes: 120,
    min_players: 1,
    max_players: 4,
    allow_sparring: false,
    is_active: true,
    sort_order: 10,
  };
}

function emptyAvailability() {
  return {
    table_number: "",
    table_id: "",
    day_of_week: 1,
    start_time: "09:00",
    end_time: "21:00",
    allowed_offering_ids: [],
    is_active: true,
  };
}

export default function TableRental() {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const clubId = user?.club_id || user?.club_details?.club_id;
  const lang = i18n.language?.slice(0, 2) || "en";

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [offerings, setOfferings] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [clubTables, setClubTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [rentalEnabled, setRentalEnabled] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [clubBookingLinks, setClubBookingLinks] = useState({
    club_subdomain: null,
    club_code: null,
    club_name: null,
  });
  const [offeringForm, setOfferingForm] = useState(emptyOffering());
  const [availabilityForm, setAvailabilityForm] = useState(emptyAvailability());
  const [editOfferingOpen, setEditOfferingOpen] = useState(false);

  const bookingUrls = useMemo(() => {
    const sub =
      clubBookingLinks.club_subdomain ||
      brand?.club_subdomain ||
      null;
    const clubCode =
      clubBookingLinks.club_code ||
      clubBookingLinks.club_id ||
      clubId ||
      null;

    if (sub) {
      const base = `https://${sub}.${brandSubdomainBase}`;
      return {
        embed: `${base}/book?embed=1`,
        direct: `${base}/book`,
      };
    }

    if (clubCode && typeof window !== "undefined") {
      const basename = getRouterBasename().replace(/\/$/, "");
      const path = `${basename}/book/${encodeURIComponent(clubCode)}`.replace(/\/+/g, "/");
      const origin = window.location.origin;
      return {
        embed: `${origin}${path}?embed=1`,
        direct: `${origin}${path}`,
      };
    }

    return null;
  }, [clubBookingLinks, brand, clubId]);

  const embedUrl = bookingUrls?.embed || "";
  const directUrl = bookingUrls?.direct || "";

  const loadAll = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const [offRes, avRes, bkRes, stRes, settingsRes, tablesRes] = await Promise.all([
        adminListOfferings(clubId, lang),
        adminListAvailabilities(clubId),
        adminListBookings(clubId),
        adminStripeStatus(clubId).catch(() => ({ data: { stripe_onboarding_complete: false } })),
        adminGetRentalSettings(clubId).catch(() => ({ data: { rental_enabled: false } })),
        adminListClubTables(clubId).catch(() => ({ data: [] })),
      ]);
      setOfferings(offRes.data || []);
      setAvailabilities(avRes.data || []);
      setBookings(bkRes.data || []);
      setClubTables(tablesRes.data || []);
      setStripeReady(!!stRes.data?.stripe_onboarding_complete);
      setRentalEnabled(!!settingsRes.data?.rental_enabled);
      if (settingsRes.data) {
        setClubBookingLinks({
          club_subdomain: settingsRes.data.club_subdomain || brand?.club_subdomain || null,
          club_code: settingsRes.data.club_code || null,
          club_name: settingsRes.data.club_name || null,
          club_id: settingsRes.data.club_id || clubId,
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }, [clubId, lang, brand?.club_subdomain]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveOffering = async () => {
    if ((offeringForm.duration_max_minutes || 60) < (offeringForm.duration_min_minutes || 60)) {
      setMessage({ type: "error", text: t("rental_duration_invalid") });
      return;
    }
    try {
      await adminSaveOffering(clubId, offeringForm);
      setEditOfferingOpen(false);
      setOfferingForm(emptyOffering());
      setMessage({ type: "success", text: t("rental_saved") });
      loadAll();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || err.message });
    }
  };

  const handleSaveAvailability = async () => {
    if (!availabilityForm.table_number && !availabilityForm.table_id) {
      setMessage({ type: "error", text: t("rental_select_table") });
      return;
    }
    try {
      const selected = clubTables.find(
        (tbl) => String(tbl.table_number) === String(availabilityForm.table_number)
      );
      await adminSaveAvailability(clubId, {
        ...availabilityForm,
        table_number: availabilityForm.table_number,
        table_id: selected?.table_id,
        troom_id: selected?.troom_id,
      });
      setAvailabilityForm(emptyAvailability());
      setMessage({ type: "success", text: t("rental_saved") });
      loadAll();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || err.message });
    }
  };

  const handleToggleRental = async (enabled) => {
    await adminUpdateRentalSettings(clubId, { rental_enabled: enabled });
    setRentalEnabled(enabled);
  };

  const handleStripeOnboard = async () => {
    const res = await adminStripeOnboarding(clubId);
    if (res.data?.url) window.location.href = res.data.url;
  };

  const copyEmbed = () => {
    if (!embedUrl) {
      setMessage({ type: "warning", text: t("rental_embed_link_missing") });
      return;
    }
    const snippet = `<iframe src="${embedUrl}" width="100%" height="900" frameborder="0" title="Book a table"></iframe>`;
    navigator.clipboard.writeText(snippet);
    setMessage({ type: "success", text: t("rental_embed_copied") });
  };

  if (!clubId) {
    return <Alert severity="warning">{t("rental_no_club")}</Alert>;
  }

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <SideBar />
      <Box id="content" sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <TopBar />
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            {t("rental_admin_title")}
          </Typography>
          <Typography color="text.secondary" paragraph>
            {t("rental_admin_subtitle")}
          </Typography>

          {message.text && (
            <Alert severity={message.type || "info"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={rentalEnabled}
                  onChange={(e) => handleToggleRental(e.target.checked)}
                />
              }
              label={t("rental_enable_feature")}
            />
            <Chip
              label={stripeReady ? t("rental_stripe_ready") : t("rental_stripe_pending")}
              color={stripeReady ? "success" : "warning"}
              sx={{ ml: 2 }}
            />
          </Paper>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={t("rental_tab_offerings")} />
            <Tab label={t("rental_tab_availability")} />
            <Tab label={t("rental_tab_bookings")} />
            <Tab label={t("rental_tab_payments")} />
            <Tab label={t("rental_tab_integration")} />
          </Tabs>

          {loading ? (
            <CircularProgress />
          ) : (
            <>
              {tab === 0 && (
                <Box>
                  <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
                    <Button variant="contained" onClick={() => { setOfferingForm(emptyOffering()); setEditOfferingOpen(true); }}>
                      {t("rental_add_custom_package")}
                    </Button>
                    <Button variant="outlined" onClick={() => adminSeedOfferings(clubId).then(loadAll)}>
                      {t("rental_seed_defaults")}
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    {offerings.map((o) => (
                      <Grid item xs={12} md={6} key={o.offering_id}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6">{o.name || o.name_en}</Typography>
                          <Typography variant="body2" color="text.secondary">{o.service_type}</Typography>
                          <Typography sx={{ mt: 1 }}>{(o.price_cents / 100).toFixed(2)} EUR/h</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t("rental_session_duration")}: {formatDurationDisplay(o)} {t("rental_minutes_abbr")}
                          </Typography>
                          <Chip label={o.is_active ? t("active") : t("inactive")} size="small" sx={{ mt: 1, mr: 1 }} />
                          {o.allow_sparring && <Chip label={t("rental_sparring")} size="small" />}
                          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                            <Button size="small" onClick={() => { setOfferingForm(o); setEditOfferingOpen(true); }}>
                              {t("update")}
                            </Button>
                            {o.service_type === "CUSTOM" && (
                              <Button size="small" color="error" onClick={() => adminDeleteOffering(clubId, o.offering_id).then(loadAll)}>
                                {t("delete")}
                              </Button>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {tab === 1 && (
                <Box>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          label={t("rental_table_number")}
                          value={availabilityForm.table_number}
                          onChange={(e) =>
                            setAvailabilityForm({
                              ...availabilityForm,
                              table_number: e.target.value,
                            })
                          }
                          SelectProps={{ native: true }}
                          fullWidth
                          helperText={
                            clubTables.length === 0 ? t("rental_no_tables_configured") : ""
                          }
                        >
                          <option value="">{t("rental_select_table")}</option>
                          {clubTables.map((tbl) => (
                            <option key={tbl.table_id} value={tbl.table_number}>
                              {t("rental_table_option", {
                                number: tbl.table_number,
                                room: tbl.troom_name || "",
                              })}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField select label={t("rental_day")} value={availabilityForm.day_of_week} onChange={(e) => setAvailabilityForm({ ...availabilityForm, day_of_week: Number(e.target.value) })} SelectProps={{ native: true }} fullWidth>
                          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField label={t("rental_start")} value={availabilityForm.start_time} onChange={(e) => setAvailabilityForm({ ...availabilityForm, start_time: e.target.value })} fullWidth />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField label={t("rental_end")} value={availabilityForm.end_time} onChange={(e) => setAvailabilityForm({ ...availabilityForm, end_time: e.target.value })} fullWidth />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button variant="contained" onClick={handleSaveAvailability} fullWidth sx={{ height: "100%" }}>
                          {t("rental_add_availability")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  {availabilities.map((a) => (
                    <Paper key={a.availability_id} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between" }}>
                      <Typography>
                        {DAYS[a.day_of_week]} · {t("rental_table_label", {
                          number: a.table?.table_number ?? "?",
                          room: a.table?.trainingRoom?.troom_name || "",
                        })} · {a.start_time}–{a.end_time}
                      </Typography>
                      <Button size="small" color="error" onClick={() => adminDeleteAvailability(clubId, a.availability_id).then(loadAll)}>
                        {t("delete")}
                      </Button>
                    </Paper>
                  ))}
                </Box>
              )}

              {tab === 2 && (
                <Box>
                  {bookings.map((b) => (
                    <Paper key={b.booking_id} sx={{ p: 2, mb: 1 }}>
                      <Typography variant="subtitle1">
                        {b.creator_name} · {new Date(b.starts_at).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {b.status} · {b.booking_mode} · {b.creator_email}
                        {b.opponent_email ? ` vs ${b.opponent_email}` : ""}
                      </Typography>
                      {["pending_payment", "open_challenge", "confirmed"].includes(b.status) && (
                        <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => adminCancelBooking(clubId, b.booking_id).then(loadAll)}>
                          {t("rental_cancel_booking")}
                        </Button>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}

              {tab === 3 && (
                <Paper sx={{ p: 3 }}>
                  <Typography paragraph>{t("rental_stripe_description")}</Typography>
                  <Button variant="contained" onClick={handleStripeOnboard}>
                    {stripeReady ? t("rental_stripe_manage") : t("rental_stripe_connect")}
                  </Button>
                  <Button variant="outlined" sx={{ ml: 2 }} onClick={() => adminStripeStatus(clubId).then((r) => setStripeReady(!!r.data?.stripe_onboarding_complete))}>
                    {t("rental_refresh_status")}
                  </Button>
                </Paper>
              )}

              {tab === 4 && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>{t("rental_embed_title")}</Typography>
                  <Typography paragraph>{t("rental_embed_help")}</Typography>
                  {!bookingUrls ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("rental_embed_link_missing")}
                    </Alert>
                  ) : (
                    <>
                      {clubBookingLinks.club_subdomain && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t("rental_embed_subdomain_hint", {
                            subdomain: clubBookingLinks.club_subdomain,
                            base: brandSubdomainBase,
                          })}
                        </Typography>
                      )}
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={`<iframe src="${embedUrl}" width="100%" height="900" frameborder="0" title="Book a table"></iframe>`}
                        InputProps={{ readOnly: true }}
                        sx={{ mb: 2 }}
                      />
                      <Button startIcon={<ContentCopyIcon />} variant="contained" onClick={copyEmbed}>
                        {t("rental_copy_embed")}
                      </Button>
                      <Typography sx={{ mt: 2 }}>
                        {t("rental_direct_link")}:{" "}
                        <a href={directUrl} target="_blank" rel="noopener noreferrer">
                          {directUrl}
                        </a>
                      </Typography>
                    </>
                  )}
                </Paper>
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog open={editOfferingOpen} onClose={() => setEditOfferingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{offeringForm.offering_id ? t("update") : t("rental_add_custom_package")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField select label={t("rental_service_type")} value={offeringForm.service_type} onChange={(e) => setOfferingForm({ ...offeringForm, service_type: e.target.value })} SelectProps={{ native: true }} fullWidth disabled={offeringForm.offering_id && offeringForm.service_type !== "CUSTOM"}>
                {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label={`${t("name")} (EN)`} value={offeringForm.name_en || ""} onChange={(e) => setOfferingForm({ ...offeringForm, name_en: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label={`${t("name")} (FR)`} value={offeringForm.name_fr || ""} onChange={(e) => setOfferingForm({ ...offeringForm, name_fr: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label={t("rental_price_cents")} type="number" value={offeringForm.price_cents} onChange={(e) => setOfferingForm({ ...offeringForm, price_cents: Number(e.target.value) })} fullWidth />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField label={t("rental_min_players")} type="number" value={offeringForm.min_players} onChange={(e) => setOfferingForm({ ...offeringForm, min_players: Number(e.target.value) })} fullWidth />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField label={t("rental_max_players")} type="number" value={offeringForm.max_players} onChange={(e) => setOfferingForm({ ...offeringForm, max_players: Number(e.target.value) })} fullWidth />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label={t("rental_duration_min")}
                type="number"
                value={offeringForm.duration_min_minutes ?? 60}
                onChange={(e) =>
                  setOfferingForm({
                    ...offeringForm,
                    duration_min_minutes: Number(e.target.value),
                  })
                }
                inputProps={{ min: 15, step: 15 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label={t("rental_duration_max")}
                type="number"
                value={offeringForm.duration_max_minutes ?? 120}
                onChange={(e) =>
                  setOfferingForm({
                    ...offeringForm,
                    duration_max_minutes: Number(e.target.value),
                  })
                }
                inputProps={{ min: 15, step: 15 }}
                fullWidth
                helperText={t("rental_duration_max_help")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label={t("description")} multiline rows={2} value={offeringForm.description_en || ""} onChange={(e) => setOfferingForm({ ...offeringForm, description_en: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={!!offeringForm.allow_sparring} onChange={(e) => setOfferingForm({ ...offeringForm, allow_sparring: e.target.checked })} />} label={t("rental_allow_sparring")} />
              <FormControlLabel control={<Switch checked={!!offeringForm.is_active} onChange={(e) => setOfferingForm({ ...offeringForm, is_active: e.target.checked })} />} label={t("active")} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOfferingOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleSaveOffering}>{t("save")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
